const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
export const UPLOADS_BASE_URL = API_URL.replace('/api', '');

// ── Token management (in-memory only — never persisted to localStorage) ───────
let _accessToken = null;
let _refreshPromise = null;

export function setAccessToken(token) {
    _accessToken = token;
}

export function getAccessToken() {
    return _accessToken;
}

export function clearAccessToken() {
    _accessToken = null;
}

// ── Core fetch helpers ────────────────────────────────────────────────────────

const authHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (_accessToken) headers['Authorization'] = `Bearer ${_accessToken}`;
    return headers;
};

const authHeadersNoJson = () => {
    const headers = {};
    if (_accessToken) headers['Authorization'] = `Bearer ${_accessToken}`;
    return headers;
};

const handle = async (res) => {
    if (!res.ok) {
        const text = await res.text();
        let message;
        try {
            message = JSON.parse(text).error;
        } catch {
            message = text;
        }
        throw new Error(message || `Request failed (${res.status})`);
    }
    return res.json();
};

// Silent refresh: call /api/auth/refresh using the httpOnly cookie.
// Returns the new access token or null on failure.
async function silentRefresh() {
    try {
        const res = await fetch(`${API_URL}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
        });
        if (!res.ok) return null;
        const data = await res.json();
        _accessToken = data.token;
        return data.token;
    } catch {
        return null;
    }
}

// Deduplicated silent refresh — multiple 401s won't fire concurrent refreshes.
function deduplicatedRefresh() {
    if (!_refreshPromise) {
        _refreshPromise = silentRefresh().finally(() => {
            _refreshPromise = null;
        });
    }
    return _refreshPromise;
}

// Fetch wrapper that retries once on 401 after a silent refresh.
async function authFetch(url, options = {}) {
    options.credentials = 'include';
    if (!options.headers) options.headers = {};
    if (_accessToken) options.headers['Authorization'] = `Bearer ${_accessToken}`;

    let res = await fetch(url, options);

    if (res.status === 401) {
        const newToken = await deduplicatedRefresh();
        if (newToken) {
            options.headers['Authorization'] = `Bearer ${newToken}`;
            res = await fetch(url, options);
        }
    }

    return res;
}

/**
 * Uploads a file using the presigned URL flow (init → PUT → confirm).
 * Falls back to direct multipart upload if the presigned endpoint is unavailable.
 */
async function uploadFilePresigned(file, onProgress) {
    // Step 1: Request a presigned upload URL
    let initRes;
    try {
        initRes = await authFetch(`${API_URL}/upload/init`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({
                filename: file.name,
                size: file.size,
                content_type: file.type || 'application/octet-stream',
            }),
        });
    } catch {
        return uploadFileDirect(file);
    }

    if (initRes.status === 404 || initRes.status === 503) {
        return uploadFileDirect(file);
    }

    const initData = await handle(initRes);

    // Step 2: PUT the file directly to S3/B2 using the presigned URL
    const putRes = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', initData.upload_url);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

        if (onProgress) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    onProgress({ loaded: e.loaded, total: e.total });
                }
            };
        }

        xhr.onload = () => resolve(xhr);
        xhr.onerror = () => reject(new Error('Upload to storage failed'));
        xhr.send(file);
    });

    if (putRes.status < 200 || putRes.status >= 300) {
        throw new Error(`Storage upload failed (${putRes.status})`);
    }

    // Step 3: Confirm the upload
    return authFetch(`${API_URL}/upload/confirm`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ file_id: initData.file_id }),
    }).then(handle);
}

/** Direct multipart upload — the legacy/ECS path. */
function uploadFileDirect(file) {
    const formData = new FormData();
    formData.append('file', file);
    return authFetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: authHeadersNoJson(),
        body: formData,
    }).then(handle);
}

export const api = {
    // ── Auth ──────────────────────────────────────────────────────────────────

    login: (email, password) =>
        fetch(`${API_URL}/login`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        }).then(handle),

    // Silent refresh using httpOnly cookie
    refresh: () => deduplicatedRefresh(),

    logout: () =>
        fetch(`${API_URL}/auth/logout`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
        }).catch(() => { /* best-effort */ }),

    validateInvite: (token) =>
        fetch(`${API_URL}/invite/${token}`).then(handle),

    registerWithInvite: (data) =>
        fetch(`${API_URL}/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        }).then(handle),

    createInvite: (data) =>
        authFetch(`${API_URL}/admin/invites`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handle),

    listInvites: () =>
        authFetch(`${API_URL}/admin/invites`, { headers: authHeadersNoJson() }).then(handle),

    revokeInvite: (id) =>
        authFetch(`${API_URL}/admin/invites/${id}`, { method: 'DELETE', headers: authHeadersNoJson() }).then(handle),

    // ── Profile ───────────────────────────────────────────────────────────────

    getProfile: () =>
        authFetch(`${API_URL}/me`, { headers: authHeadersNoJson() }).then(handle),

    listFiles: () =>
        authFetch(`${API_URL}/files`, { headers: authHeadersNoJson() }).then(handle),

    uploadFile: (fileOrFormData, _token, onProgress) => {
        const file = fileOrFormData instanceof File ? fileOrFormData : fileOrFormData.get('file');
        if (file) {
            return uploadFilePresigned(file, onProgress);
        }
        return authFetch(`${API_URL}/upload`, { method: 'POST', headers: authHeadersNoJson(), body: fileOrFormData }).then(handle);
    },

    getFileUrl: (id) =>
        authFetch(`${API_URL}/files/${id}/url`, { headers: authHeadersNoJson() }).then(handle).then(d => d.url),

    getFileUrls: (fileIds) =>
        authFetch(`${API_URL}/files/urls`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ file_ids: fileIds }) }).then(handle).then(d => ({ urls: d.urls || {}, thumbnailUrls: d.thumbnail_urls || {} })),

    deleteFile: (id) =>
        authFetch(`${API_URL}/files/${id}`, { method: 'DELETE', headers: authHeadersNoJson() }).then(handle),

    getStats: () =>
        authFetch(`${API_URL}/admin/stats`, { headers: authHeadersNoJson() }).then(handle),

    registerUser: (data) =>
        authFetch(`${API_URL}/admin/register`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handle),

    updateQuota: (bytes) =>
        authFetch(`${API_URL}/me/quota`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ quota_bytes: bytes }) }).then(handle),

    listStorageEndpoints: () =>
        authFetch(`${API_URL}/storage/endpoints`, { headers: authHeadersNoJson() }).then(handle),

    addStorageEndpoint: (data) =>
        authFetch(`${API_URL}/storage/endpoints`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handle),

    removeStorageEndpoint: (id) =>
        authFetch(`${API_URL}/storage/endpoints/${id}`, { method: 'DELETE', headers: authHeadersNoJson() }).then(handle),

    updateSettings: (data) =>
        authFetch(`${API_URL}/me/settings`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handle),

    changePassword: (currentPassword, newPassword) =>
        authFetch(`${API_URL}/me/password`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }) }).then(handle),

    updateAdminUserQuotaMax: (userId, maxBytes) =>
        authFetch(`${API_URL}/admin/users/${userId}/quota-max`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ quota_max_bytes: maxBytes }) }).then(handle),

    listUsers: () =>
        authFetch(`${API_URL}/admin/users`, { headers: authHeadersNoJson() }).then(handle),

    updateUserPlan: (userId, plan) =>
        authFetch(`${API_URL}/admin/users/${userId}/plan`, {
            method: 'PATCH', headers: authHeaders(),
            body: JSON.stringify({ plan_tier: plan }),
        }).then(handle),

    deleteMyAccount: () =>
        authFetch(`${API_URL}/me`, {
            method: 'DELETE', headers: authHeadersNoJson(),
        }).then(handle),

    deleteUser: (userId) =>
        authFetch(`${API_URL}/admin/users/${userId}`, {
            method: 'DELETE', headers: authHeadersNoJson(),
        }).then(handle),

    getPendingDeletions: () =>
        authFetch(`${API_URL}/admin/pending-deletions`, { headers: authHeadersNoJson() }).then(handle),

    cancelDeletion: (userId) =>
        authFetch(`${API_URL}/admin/users/${userId}/cancel-deletion`, {
            method: 'POST', headers: authHeadersNoJson(),
        }).then(handle),

    purgeUserNow: (userId) =>
        authFetch(`${API_URL}/admin/users/${userId}/purge`, {
            method: 'DELETE', headers: authHeadersNoJson(),
        }).then(handle),

    getNotifications: () =>
        authFetch(`${API_URL}/notifications`, { headers: authHeadersNoJson() }).then(handle),

    markNotificationRead: (id) =>
        authFetch(`${API_URL}/notifications/${id}/read`, { method: 'PATCH', headers: authHeadersNoJson() }).then(handle),

    markAllNotificationsRead: () =>
        authFetch(`${API_URL}/notifications/read-all`, { method: 'POST', headers: authHeadersNoJson() }).then(handle),

    clearNotifications: () =>
        authFetch(`${API_URL}/notifications`, { method: 'DELETE', headers: authHeadersNoJson() }).then(handle),

    requestQuotaUpgrade: (requestedBytes) =>
        authFetch(`${API_URL}/me/quota-request`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ requested_bytes: requestedBytes }) }).then(handle),

    getPendingQuotaRequest: () =>
        authFetch(`${API_URL}/me/quota-request`, { headers: authHeadersNoJson() }).then(handle),

    listQuotaRequests: () =>
        authFetch(`${API_URL}/admin/quota-requests`, { headers: authHeadersNoJson() }).then(handle),

    reviewQuotaRequest: (id, status, adminNote) =>
        authFetch(`${API_URL}/admin/quota-requests/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status, admin_note: adminNote || '' }) }).then(handle),

    listEmailSuppressions: () =>
        authFetch(`${API_URL}/admin/email-suppressions`, { headers: authHeadersNoJson() }).then(handle),

    deleteEmailSuppression: (id) =>
        authFetch(`${API_URL}/admin/email-suppressions/${id}`, { method: 'DELETE', headers: authHeadersNoJson() }).then(handle),

    // ── Devices ─────────────────────────────────────────────────────────────

    listDevices: () =>
        authFetch(`${API_URL}/devices`, { headers: authHeadersNoJson() }).then(handle),

    registerDevice: (data) =>
        authFetch(`${API_URL}/devices`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handle),

    deleteDevice: (id) =>
        authFetch(`${API_URL}/devices/${id}`, { method: 'DELETE', headers: authHeadersNoJson() }).then(handle),

    // ── Sync ────────────────────────────────────────────────────────────────

    checkHashes: (hashes) =>
        authFetch(`${API_URL}/sync/check-hashes`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ hashes }) }).then(handle),

    checkFingerprints: (fingerprints) =>
        authFetch(`${API_URL}/sync/check-fingerprints`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ fingerprints }) }).then(handle),

    pollSyncEvents: (cursor = 0, limit = 100) =>
        authFetch(`${API_URL}/sync/events?cursor=${cursor}&limit=${limit}`, { headers: authHeadersNoJson() }).then(handle),
};
